import { useCallback, useRef, useState } from 'react'
import {
  initialRealtimeFeatures,
  keyPointsColumnOpen,
  loadRealtimeFeatures,
  parseRealtimeFeatures,
  readReadyFeatures,
  realtimeFeatureDiff,
  saveRealtimeFeatures,
  type RealtimeFeatures,
  type RealtimeFeaturesReady,
} from '@/utils/realtimeFeatures'

export type RealtimeFeatureErrorCode = 'bad_request' | 'not_supported'

interface UseRealtimeFeatureTogglesOptions {
  /** Room or meeting id the socket config uses. Null before a meeting exists. */
  meetingId: string | null | undefined
  /** Whether Summary starts open for this Mode. */
  initialMemoOpen: boolean
  /** Whether the Key Points column starts open for this Mode. */
  initialKeyPointsOpen: boolean
  /** Whether the Enhanced column starts open for this Mode. */
  initialBatchOpen: boolean
  /**
   * Send `{ event: 'features.update' }` on every socket this screen owns.
   *
   * @returns False when no socket was ready to take the frame.
   */
  sendFeaturesUpdate: (features: Partial<RealtimeFeatures>) => boolean
  /** Toast after `features.update.error`. The pane has already been put back. */
  onUpdateError: (code: RealtimeFeatureErrorCode) => void
}

/**
 * Drive the realtime switches from the panes that already fold.
 *
 * Summary is `mainContent`. The Key Points column is both `keyPoints` and
 * `confirmItems`, because those two panes share one hide button. Enhanced mode
 * is `enhanced`. Closing Copilot turns `assist` off; opening it turns `assist`
 * back on. A pane hides as soon as the reader clicks, and `ready` / the ack are
 * what the next `config` will repeat.
 */
export function useRealtimeFeatureToggles({
  meetingId,
  initialMemoOpen,
  initialKeyPointsOpen,
  initialBatchOpen,
  sendFeaturesUpdate,
  onUpdateError,
}: UseRealtimeFeatureTogglesOptions) {
  const [features, setFeatures] = useState<RealtimeFeatures>(() =>
    initialRealtimeFeatures({
      memoOpen: initialMemoOpen,
      keyPointsOpen: initialKeyPointsOpen,
      batchOpen: initialBatchOpen,
      stored: meetingId ? loadRealtimeFeatures(meetingId) : null,
    })
  )
  const [assistLocked, setAssistLocked] = useState(false)
  const featuresRef = useRef(features)
  const meetingIdRef = useRef(meetingId)
  const sendRef = useRef(sendFeaturesUpdate)
  const onUpdateErrorRef = useRef(onUpdateError)
  const ackedRef = useRef(features)
  const supportedRef = useRef(false)
  const socketReadyRef = useRef(false)
  const inflightRef = useRef(false)
  const dirtyRef = useRef(false)
  const revertingRef = useRef(false)
  const assistLockedRef = useRef(false)

  featuresRef.current = features
  meetingIdRef.current = meetingId
  sendRef.current = sendFeaturesUpdate
  onUpdateErrorRef.current = onUpdateError

  const commit = useCallback((next: RealtimeFeatures) => {
    featuresRef.current = next
    setFeatures(next)
    const id = meetingIdRef.current
    if (id) saveRealtimeFeatures(id, next)
  }, [])

  const flush = useCallback(() => {
    if (!supportedRef.current || !socketReadyRef.current) return
    if (inflightRef.current) {
      dirtyRef.current = true
      return
    }
    const partial = realtimeFeatureDiff(ackedRef.current, featuresRef.current)
    if (!partial) {
      dirtyRef.current = false
      return
    }
    if (!sendRef.current(partial)) return
    inflightRef.current = true
    dirtyRef.current = false
  }, [])

  const toggleMemo = useCallback(() => {
    const current = featuresRef.current
    commit({ ...current, mainContent: !current.mainContent })
    flush()
  }, [commit, flush])

  const toggleKeyPoints = useCallback(() => {
    const current = featuresRef.current
    const next = !(current.keyPoints || current.confirmItems)
    commit({ ...current, keyPoints: next, confirmItems: next })
    flush()
  }, [commit, flush])

  const toggleBatch = useCallback(() => {
    const current = featuresRef.current
    commit({ ...current, enhanced: !current.enhanced })
    flush()
  }, [commit, flush])

  /**
   * Turn assist on or off because Copilot was opened or closed.
   *
   * A server that already refused assist ignores an attempt to turn it on.
   *
   * @param open True when the reader opened Copilot.
   */
  const setAssist = useCallback(
    (open: boolean) => {
      if (open && assistLockedRef.current) return
      if (featuresRef.current.assist === open) return
      commit({ ...featuresRef.current, assist: open })
      flush()
    },
    [commit, flush]
  )

  /** Flags to put on the next `config`, including a reconnect. */
  const getFeatures = useCallback((): RealtimeFeatures => featuresRef.current, [])

  /**
   * Apply `ready`. No `features` field means an older backend: folding stays local.
   *
   * A later `ready` that does carry the map still turns the switches on. The
   * server map becomes the baseline, then any fold the reader made after
   * `config` was sent is pushed with `features.update`.
   *
   * @param event The `ready` frame.
   */
  const handleReady = useCallback(
    (event: { features?: unknown }) => {
      const ready: RealtimeFeaturesReady = readReadyFeatures(event)
      socketReadyRef.current = true
      if (!ready.supported) return
      supportedRef.current = true
      ackedRef.current = ready.features
      if (featuresRef.current.assist && !ready.features.assist) {
        assistLockedRef.current = true
        setAssistLocked(true)
        commit({ ...featuresRef.current, assist: false })
      }
      flush()
    },
    [commit, flush]
  )

  /**
   * Apply `features.updated`. The ack replaces anything still in flight.
   *
   * @param event The ack frame. Ignored when `features` is not a full map.
   */
  const handleUpdated = useCallback(
    (event: { features?: unknown }) => {
      const server = parseRealtimeFeatures(event.features)
      if (!server) return
      revertingRef.current = false
      inflightRef.current = false
      ackedRef.current = server
      if (!dirtyRef.current) commit(server)
      flush()
    },
    [commit, flush]
  )

  /**
   * Put the panes back after `features.update.error` and tell the other sockets.
   *
   * @param code `not_supported` locks assist; anything else is a bad request.
   */
  const handleError = useCallback(
    (code: RealtimeFeatureErrorCode) => {
      inflightRef.current = false
      dirtyRef.current = false
      if (code === 'not_supported') {
        assistLockedRef.current = true
        setAssistLocked(true)
      }
      commit(ackedRef.current)
      onUpdateErrorRef.current(code)
      if (revertingRef.current) {
        revertingRef.current = false
        return
      }
      revertingRef.current = true
      if (!sendRef.current(ackedRef.current)) revertingRef.current = false
    },
    [commit]
  )

  return {
    memoOpen: features.mainContent,
    keyPointsOpen: keyPointsColumnOpen(features),
    batchOpen: features.enhanced,
    assistOn: features.assist && !assistLocked,
    assistLocked,
    toggleMemo,
    toggleKeyPoints,
    toggleBatch,
    setAssist,
    getFeatures,
    handleReady,
    handleUpdated,
    handleError,
  }
}
